import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useState } from "react";

import {
  Form,
  FormMessage,
  FormLabel,
  FormItem,
  FormField,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";

import { usePostViability } from "@/queries/mutations/use-post-viability";
import { usePatchViability } from "@/queries/mutations/use-patch-viability";

import { validFormValues } from "./viability-calculator";

import { viabilityCreationSchema } from "@/schemas/viabilities";

import { newId } from "@/constants";

import type { Viability, ViabilityProperty } from "@/types";

interface SaveCalculatorDialogProps
  extends Partial<Pick<Viability, "id" | "titulo">> {
  leilaoId: Viability["leilao_id"];
  children: React.ReactNode;
  avoidSave?: boolean;
  data: ViabilityProperty;
  onSave?: () => void;
}

export const SaveViabilityCalculatorDialog = ({
  children,
  avoidSave,
  id,
  leilaoId,
  titulo,
  data,
  onSave,
}: SaveCalculatorDialogProps) => {
  if (avoidSave) return children;

  const [open, setOpen] = useState(false);

  const form = useForm({
    resolver: zodResolver(viabilityCreationSchema),
    defaultValues: {
      titulo: titulo || "",
    },
  });

  const postMutation = usePostViability();
  const patchMutation = usePatchViability();

  const submit = async ({ titulo }: { titulo: string }) => {
    const processedValues = validFormValues(data);

    if (id && id !== newId) {
      await patchMutation.mutateAsync({
        ...processedValues,
        id,
        titulo,
      });
    } else {
      await postMutation.mutateAsync({
        ...processedValues,
        leilao_id: leilaoId,
        titulo,
      });
    }

    setOpen(false);
    onSave?.();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent aria-describedby={undefined} className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{id ? "Editar cálculo" : "Salvar cálculo"}</DialogTitle>
          <DialogDescription>
            {id
              ? "Edite seu cálculo para revisá-lo posteriormente"
              : "Salve seu cálculo para revisá-lo posteriormente"}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={(event) => {
              /**
               * The issue is that when you submit the SaveViabilityCalculatorDialog form,
               * the event is bubbling up to the parent ViabilityCalculator form.
               * This happens because both forms are nested, and by default, a form
               * submission event can propagate upward.
               *
               * This is a workaround to prevent the event from bubbling up to the
               * parent form.
               */
              event.preventDefault();
              event.stopPropagation();
              form.handleSubmit(submit)(event);
            }}
            className="flex flex-col gap-12"
          >
            <FormField
              control={form.control}
              name="titulo"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Título</FormLabel>
                  <Input
                    placeholder="ex: Estudo de viabilidade de venda à vista do imóvel de São Paulo"
                    defaultValue={field.value}
                    onChange={field.onChange}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button
                type="submit"
                disabled={postMutation.isPending || patchMutation.isPending}
                className="w-full flex flex-row gap-2 items-center justify-center"
              >
                {postMutation.isPending || patchMutation.isPending ? (
                  <>
                    <Spinner inverse /> Salvando
                  </>
                ) : (
                  "Salvar cálculo"
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
